import { createContext, ReactNode, useContext, useState } from 'react'

import { Modal } from '@susu/ui/components'

interface ModalContextType {
  openModal: (content: ReactNode, title?: string) => void
  closeModal: () => void
  setTitle: (title: string | undefined) => void
  setShowHeaderBorder: (show: boolean) => void
  setShowHeader: (show: boolean) => void
  setOnBack: (handler: (() => void) | undefined) => void
  isOpen: boolean
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ReactNode>(null)
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined)
  const [showHeaderBorder, setShowHeaderBorderState] = useState(true)
  const [showHeader, setShowHeaderState] = useState(true)
  const [onBackHandler, setOnBackHandler] = useState<(() => void) | undefined>(
    undefined,
  )

  const openModal = (content: ReactNode, title?: string) => {
    setModalContent(content)
    setModalTitle(title)
    setShowHeaderBorderState(false) // Default to no border for new modals
    setShowHeaderState(true) // Default to showing header with close button
    setOnBackHandler(undefined) // Reset back handler for new modals
    setIsOpen(true)
  }

  const closeModal = () => {
    // Only set isOpen to false - don't touch any other state during the closing animation.
    // Changing state during the transition causes re-renders that can flash.
    // The Modal component handles its own animation via onTransitionEnd.
    // Content cleanup happens in openModal when a new modal is opened.
    setIsOpen(false)
  }

  const setTitle = (title: string | undefined) => {
    setModalTitle(title)
  }

  const setShowHeaderBorder = (show: boolean) => {
    setShowHeaderBorderState(show)
  }

  const setShowHeader = (show: boolean) => {
    setShowHeaderState(show)
  }

  const setOnBack = (handler: (() => void) | undefined) => {
    // Wrap in a thunk to avoid React treating the function as a state updater
    setOnBackHandler(() => handler)
  }

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        setTitle,
        setShowHeaderBorder,
        setShowHeader,
        setOnBack,
        isOpen,
      }}
    >
      {children}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        onBack={onBackHandler}
        title={modalTitle}
        showHeaderBorder={showHeaderBorder}
        showHeader={showHeader}
      >
        {modalContent}
      </Modal>
    </ModalContext.Provider>
  )
}

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}
